import { useEffect, useState } from 'react';
import { AxiosResponse } from 'axios';
import internalApiClient from '../util/api/internalApiClient';
import useQuery from './useQuery';

// Used to determine in which order should the notifications be rendered
const NOTIFICATIONS_TO_DISPLAY: number[] = [1, 21, 15, 22, 3, 2, 4, 10, 11, 12, 8, 6];

export default function useNotificationsSettings() {
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [isToggling, setIsToggling] = useState<boolean>(false);
    const { data, error, loading, refetch } = useQuery<Notification[]>('user/notification-settings');

    /** Order the fetched notifications and add them to the state */
    useEffect(() => {
        if (Array.isArray(data)) {
            const orderedNotifications = orderNotifications(data);
            setNotifications(orderedNotifications);
        }
    }, [data]);

    /**
     * Returns notifications ordered base on the order provided in NOTIFICATIONS_TO_DISPLAY
     *
     * @param notifications
     * @returns
     */
    function orderNotifications(notifications: Notification[]): Notification[] {
        const orderedNotifications = NOTIFICATIONS_TO_DISPLAY.map(notificationType =>
            notifications.find(notification => notification.type === notificationType)
        ).filter(notification => notification !== undefined);

        // Because we used filter to remove the 'undefined' values,
        // orderedNotifications should not contain any undefined values
        // so using 'as Notification[]' type should be safe
        return orderedNotifications as Notification[];
    }

    /**
     * Make a patch api call to update a specific notification
     *
     * @param notification
     * @returns
     */
    async function patchNotification(notification: PatchNotification): Promise<AxiosResponse<PatchNotification>> {
        return await internalApiClient.patch<PatchNotification>('/user/notification-settings', notification);
    }

    /**
     * Check if there is at least one notification which has a channel type
     * equal with the one provided as an argument
     *
     * @param channelType
     * @returns
     */
    function hasChannel(channelType: number): boolean {
        return notifications.some(notification => notification.channels.some(channel => channel.type === channelType));
    }

    /**
     * Returns true if for the provided channel type, all notifications are enabled
     *
     * @param channelType
     * @returns
     */
    function allEnabled(channelType: number) {
        return notifications.every(notification => {
            return notification.channels.find(ch => ch.type === channelType)?.enabled ?? true;
        });
    }

    /**
     * Toggle the status of one notification
     *
     * @param updatedNotification
     * @returns
     */
    async function toggleOne(updatedNotification: PatchNotification): Promise<void> {
        try {
            setIsToggling(true);
            await patchNotification(updatedNotification);

            const updatedNotifications = notifications.map(notification => ({
                ...notification,
                channels: notification.channels.map(ch => {
                    if (notification.type === updatedNotification.type && ch.type === updatedNotification.channel) {
                        return { ...ch, enabled: updatedNotification.enabled };
                    }
                    return ch;
                }),
            }));

            setNotifications(updatedNotifications);
        } finally {
            setIsToggling(false);
        }
    }

    /**
     * Toggle all notifications belonging to a channel
     *
     * @param channel
     * @param enabled
     * @returns
     */
    async function toggleAll(channel: number, enabled: boolean): Promise<{ toggleAllFailed: boolean }> {
        // Because there is no api endpoint to toggle all notifications at once
        // we must find those notifications for which the value of the 'enabled'
        // key is different than the provided [enabled] argument
        const notificationsToPost: PatchNotification[] = [];
        notifications.forEach(notification => {
            notification.channels.forEach(ch => {
                if (ch.type === channel && ch.enabled !== enabled) {
                    notificationsToPost.push({ channel: ch.type, enabled, type: notification.type });
                }
            });
        });

        // Create api calls for each notification that needs to be toggled
        const apiCalls = notificationsToPost.map(notification => patchNotification(notification));
        // Make the api calls
        setIsToggling(true);
        const apiCallResponses = await Promise.allSettled(apiCalls);
        // Find all api calls that succeed
        const fulfilledRes = apiCallResponses.filter(res => res.status === 'fulfilled') as PromiseFulfilledResult<
            AxiosResponse<PatchNotification>
        >[];
        // Store the notifications for which the api call succeed to update the state only for them
        const notificationsToUpdate = fulfilledRes.map(res => {
            const notification: PatchNotification = JSON.parse(res.value.config.data);
            return notification.type;
        });
        // If [notificationsToUpdate] has less elements than [apiCalls] it means that some of the api calls
        // failed
        const toggleAllFailed = apiCalls.length !== notificationsToUpdate.length;

        // Updated notifications state, updating only the ones present in [notificationsToUpdate],
        const updatedNotifications = notifications.map(notification => ({
            ...notification,
            channels: notification.channels.map(ch => {
                if (ch.type === channel && ch.enabled !== enabled && notificationsToUpdate.includes(notification.type)) {
                    return { ...ch, enabled };
                }
                return ch;
            }),
        }));

        setNotifications(updatedNotifications);
        setIsToggling(false);

        return { toggleAllFailed };
    }

    return {
        allEnabled,
        error,
        hasChannel,
        isToggling,
        loading,
        notifications,
        refetch,
        toggleAll,
        toggleOne,
    };
}

useNotificationsSettings.CHANNELS = {
    Web: 1,
    Email: 2,
} as const;

export interface Notification {
    type: number;
    channels: Channel[];
}

export interface Channel {
    type: number;
    enabled: boolean;
}

export interface PatchNotification {
    type: number;
    channel: number;
    enabled: boolean;
}

export type ChannelsMap = typeof useNotificationsSettings.CHANNELS;
